import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchData } from '../../Services/apiService.js';
import { apiUrl, courseTypeFromProgramId, registrationProgramKey } from '../../config/api.js';
import './CoursePages.css';

const CourseDetails = () => {
  const { id, courseType: routeCourseType } = useParams();
  const [events, setEvents] = useState([]);
  const [offering, setOffering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const courseType = routeCourseType || courseTypeFromProgramId(id);

  useEffect(() => {
    if (!courseType) {
      setError('Course not found.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    Promise.all([
      fetchData(apiUrl(`ByType/Courses/${courseType}`)),
      fetchData(apiUrl(`ByType/Offerings/${courseType}`)),
    ])
      .then(([sessionData, offeringData]) => {
        setEvents(sessionData || []);
        setOffering(offeringData?.[0] || null);
      })
      .catch((requestError) => {
        console.error('Error fetching events:', requestError);
        setError('Unable to load this course right now.');
      })
      .finally(() => setLoading(false));
  }, [courseType]);

  if (loading) return <main className="course-page-state">Loading course…</main>;
  if (error) return <main className="course-page-state">{error}</main>;

  const featuredCourse = events[0];
  if (!offering) return <main className="course-page-state">Course offering is not available.</main>;

  return (
    <main className="course-page">
      <header className="course-page__hero">
        <div className="container course-page__container">
          <span className="course-page__level">{offering.course_type}</span>
          <h1>{offering.title}</h1>
          <p>{offering.subtitle}</p>
        </div>
      </header>

      <div className="container course-page__container course-page__content">
        <section className="course-page__overview">
          <span className="course-page__eyebrow">Program overview</span>
          <h2>{offering.title}</h2>
          <p>{offering.overview}</p>
          {offering.highlights?.length > 0 && (
            <ul className="course-page__checklist">
              {offering.highlights.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
          )}
          {offering.sections?.map((section, index) => (
            <article className="course-page__topic" key={`${section.title}-${index}`}>
              <h3>{section.title}</h3>
              {section.items?.length > 0 && <ul>{section.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>}
            </article>
          ))}
        </section>

        <aside className="course-page__sessions">
          <div className="course-page__sessions-heading">
            <span>Live learning</span>
            <h2>Upcoming sessions</h2>
          </div>
          {featuredCourse ? events.map((event) => (
            <article className="course-session" key={event.CourseID}>
              <h3>{event.CourseTitle}</h3>
              {event.CourseSubTitle && <p>{event.CourseSubTitle}</p>}
              <dl>
                <div><dt>Date</dt><dd>{event.CourseDate}</dd></div>
                <div><dt>Time</dt><dd>{event.Time}</dd></div>
                <div><dt>Mentor</dt><dd>{event.CourseBy}</dd></div>
                <div><dt>Language</dt><dd>{event.Language}</dd></div>
                <div><dt>Fee</dt><dd>{event.Fees || 'Free'}</dd></div>
              </dl>
              <Link
                to={`/ApplyCourse/${registrationProgramKey(event.CourseType)}/${event.CourseID}`}
                className="course-session__link"
              >
                View session <span aria-hidden="true">→</span>
              </Link>
            </article>
          )) : <p className="course-page__empty">New sessions will be announced soon.</p>}
        </aside>
      </div>
    </main>
  );
};

export { CourseDetails };
